/**
 * 时区格式化工具
 * 用于将UTC时间转换为指定时区的本地时间
 */

/**
 * 解析时区字符串，支持 IANA 时区标识符和 UTC+XX:XX 格式
 * @param timezone 时区字符串
 * @returns 解析后的时区偏移量（分钟）或 null（如果是 IANA 标识符）
 */
const parseTimezoneOffset = (timezone: string): number | null => {
  // 匹配 UTC+02:00, UTC-05:00, UTC+2:00 等格式
  const utcOffsetMatch = timezone.match(/^UTC([+-])(\d{1,2}):?(\d{2})?$/i);
  if (utcOffsetMatch) {
    const sign = utcOffsetMatch[1] === '+' ? 1 : -1;
    const hours = parseInt(utcOffsetMatch[2], 10);
    const minutes = utcOffsetMatch[3] ? parseInt(utcOffsetMatch[3], 10) : 0;
    return sign * (hours * 60 + minutes);
  }
  // 匹配 UTC+2, UTC-5 等简化格式
  const simpleOffsetMatch = timezone.match(/^UTC([+-])(\d{1,2})$/i);
  if (simpleOffsetMatch) {
    const sign = simpleOffsetMatch[1] === '+' ? 1 : -1;
    const hours = parseInt(simpleOffsetMatch[2], 10);
    return sign * hours * 60;
  }
  return null;
};

/**
 * 手动格式化时间（使用偏移量）
 */
const formatTimeWithOffset = (
  date: Date,
  offsetMinutes: number,
  locale: string = 'zh-CN'
): string => {
  // 创建新日期对象，应用偏移量
  const offsetDate = new Date(date.getTime() + offsetMinutes * 60 * 1000);
  
  // 使用 UTC 方法格式化，因为我们已经手动应用了偏移量
  const year = offsetDate.getUTCFullYear();
  const month = String(offsetDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(offsetDate.getUTCDate()).padStart(2, '0');
  const hour = String(offsetDate.getUTCHours()).padStart(2, '0');
  const minute = String(offsetDate.getUTCMinutes()).padStart(2, '0');
  const second = String(offsetDate.getUTCSeconds()).padStart(2, '0');
  
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
};

/**
 * 无时区后缀时按 UTC 解析（接口常返回 "2026-02-04 08:13:21" 无 Z，JS 会按本地解析导致未转换）
 */
export const normalizeUtcString = (s: string): string => {
  const t = s.trim();
  if (/Z$/i.test(t) || /[+-]\d{2}:?\d{2}$/.test(t)) return t;
  return t.replace(' ', 'T') + 'Z';
};

/**
 * 将 UTC 时间字符串格式化为当前浏览器所在时区的本地时间
 * @param utcTimeString UTC 时间字符串（ISO 8601 或 "YYYY-MM-DD HH:mm:ss" 无后缀时按 UTC 解析）
 * @returns 格式化后的本地时间字符串，空值返回 '-'
 */
export const formatUtcToLocal = (
  utcTimeString: string | undefined | null
): string => {
  if (!utcTimeString) return '-';
  const normalized = normalizeUtcString(utcTimeString);
  const tz = typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC';
  return formatTimeWithTimezone(normalized, tz);
};

/**
 * 将 UTC 时间字符串格式化为固定 UTC 时区的 "yyyy-MM-dd HH:mm:ss"（横杠分隔）。
 * 与定价方案模块迁移前 api-server 老逻辑（`DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss")` +
 * `ZoneId.of("UTC")`）逐字符对齐：不随浏览器所在时区变化，任何人看到的都是同一个字符串。
 * 仅供定价方案相关列表（PricingSchemeTab/CostPeriodicPricingList/DSPPricingList）使用，
 * 与 {@link formatUtcToLocal}（浏览器本地时区展示）语义不同，不要混用。
 * @param utcTimeString UTC 时间字符串（ISO 8601 或 "YYYY-MM-DD HH:mm:ss" 无后缀时按 UTC 解析）
 * @returns 格式化后的固定 UTC 时间字符串，空值或非法值返回 '-'
 */
export const formatFixedUtcDateTime = (
  utcTimeString: string | undefined | null
): string => {
  if (!utcTimeString) return '-';
  const normalized = normalizeUtcString(utcTimeString);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '-';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hour = String(date.getUTCHours()).padStart(2, '0');
  const minute = String(date.getUTCMinutes()).padStart(2, '0');
  const second = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

/** 美西时区 IANA 标识符（分拣看板等明细页统一用美西时间） */
export const PACIFIC_TIMEZONE = 'America/Los_Angeles';

/** 账单时间范围可选 IANA 时区 */
export const BILL_TIMEZONE_OPTIONS = [
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' }
] as const;

/**
 * 将某个时刻格式化为指定 IANA 时区下的 "YYYY-MM-DD HH:mm:ss"
 * @param date 时刻（Date 或时间戳）
 * @param ianaTimezone IANA 时区标识符，如 'Asia/Shanghai', 'America/Los_Angeles'
 * @returns 该时区下的本地时间字符串
 */
export const formatInstantInIanaZone = (
  date: Date | number,
  ianaTimezone: string
): string => {
  const d = typeof date === 'number' ? new Date(date) : date;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const year = get('year');
  const month = get('month');
  const day = get('day');
  const hour = get('hour');
  const minute = get('minute');
  const second = get('second');
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

/**
 * 将 UTC 时间字符串格式化为美西时间
 * @param utcTimeString UTC 时间字符串（ISO 8601 或 "YYYY-MM-DD HH:mm:ss" 无后缀时按 UTC 解析）
 * @returns 格式化后的美西时间字符串，空值返回 '-'
 */
export const formatUtcToPacific = (
  utcTimeString: string | undefined | null
): string => {
  if (!utcTimeString) return '-';
  const normalized = normalizeUtcString(utcTimeString);
  return formatTimeWithTimezone(normalized, PACIFIC_TIMEZONE);
};

/**
 * 将UTC时间字符串转换为指定时区的本地时间字符串
 * @param utcTimeString UTC时间字符串（ISO 8601格式）
 * @param timezone IANA时区标识符（如 'America/New_York', 'Asia/Shanghai'）或 UTC+XX:XX 格式
 * @param locale 语言环境（默认 'zh-CN'）
 * @returns 格式化后的本地时间字符串
 */
export const formatTimeWithTimezone = (
  utcTimeString: string | undefined | null,
  timezone: string = 'UTC',
  locale: string = 'zh-CN'
): string => {
  if (!utcTimeString) {
    return '-';
  }

  try {
    const date = new Date(utcTimeString);
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', utcTimeString);
      return '-';
    }

    // 尝试解析为 UTC 偏移格式
    const offsetMinutes = parseTimezoneOffset(timezone);
    if (offsetMinutes !== null) {
      // 使用手动偏移量格式化
      return formatTimeWithOffset(date, offsetMinutes, locale);
    }

    // 使用 Intl.DateTimeFormat 进行时区转换和格式化（适用于 IANA 时区标识符）
    try {
      // 使用 formatToParts 获取各个部分，然后手动拼接，确保格式统一为横杠
      const formatter = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(date);
      
      // 从 parts 中提取各个部分并手动拼接为斜杠格式
      const year = parts.find(p => p.type === 'year')?.value || '';
      const month = parts.find(p => p.type === 'month')?.value || '';
      const day = parts.find(p => p.type === 'day')?.value || '';
      const hour = parts.find(p => p.type === 'hour')?.value || '';
      const minute = parts.find(p => p.type === 'minute')?.value || '';
      const second = parts.find(p => p.type === 'second')?.value || '';
      
      return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
    } catch (intlError) {
      // 如果 Intl API 不支持该时区，尝试使用偏移量格式
      console.warn('Intl.DateTimeFormat failed, falling back to UTC:', intlError);
      return formatTimeWithOffset(date, 0, locale);
    }
  } catch (error) {
    console.error('Error formatting time with timezone:', error, {
      utcTimeString,
      timezone
    });
    return utcTimeString;
  }
};

/**
 * 按记录自身时区（IANA 或裸偏移量，如 "America/New_York"/"+8"）格式化为 "yyyy-MM-dd HH:mm:ss"（横杠分隔）。
 * 与定价方案绑定模块迁移前 api-server 老逻辑（`parseApplyTimezone` +
 * `DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss")`）逐字符对齐：时区仍取记录自身的
 * timezone 字段（不是浏览器本地时区，这点跟 {@link formatUtcToLocal} 不同），只是把
 * {@link formatTimeWithTimezone} 固定输出的斜杠分隔符换成老系统的横杠分隔符。
 * 仅供定价方案绑定相关列表（PricingSchemeApplyColumns/DSPPricingApplyColumns/CostPeriodicApplyPage/
 * ApplySchemeComp/DSPApplySchemeComp/CostPeriodicBindEditComp/CostPeriodicUnbindSchemeComp）使用。
 * @param utcTimeString UTC 时间字符串
 * @param timezone 记录自身的 timezone 字段
 * @returns 该时区下 "yyyy-MM-dd HH:mm:ss" 格式的字符串，空值或非法值返回 '-' 或原始值
 */
export const formatApplyTimeWithTimezone = (
  utcTimeString: string | undefined | null,
  timezone: string
): string => formatTimeWithTimezone(utcTimeString, timezone).replace(/\//g, '-');

/**
 * 手动格式化日期（使用偏移量）
 */
const formatDateWithOffset = (
  date: Date,
  offsetMinutes: number,
  locale: string = 'zh-CN'
): string => {
  // 创建新日期对象，应用偏移量
  const offsetDate = new Date(date.getTime() + offsetMinutes * 60 * 1000);
  
  // 使用 UTC 方法格式化，因为我们已经手动应用了偏移量
  const year = offsetDate.getUTCFullYear();
  const month = String(offsetDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(offsetDate.getUTCDate()).padStart(2, '0');
  
  return `${year}/${month}/${day}`;
};

/**
 * 格式化时间（仅日期部分）
 * @param utcTimeString UTC时间字符串
 * @param timezone 时区（支持 IANA 标识符或 UTC+XX:XX 格式）
 * @param locale 语言环境
 * @returns 格式化后的日期字符串
 */
export const formatDateWithTimezone = (
  utcTimeString: string | undefined | null,
  timezone: string = 'UTC',
  locale: string = 'zh-CN'
): string => {
  if (!utcTimeString) {
    return '-';
  }

  try {
    const date = new Date(utcTimeString);
    
    if (isNaN(date.getTime())) {
      return '-';
    }

    // 尝试解析为 UTC 偏移格式
    const offsetMinutes = parseTimezoneOffset(timezone);
    if (offsetMinutes !== null) {
      // 使用手动偏移量格式化
      return formatDateWithOffset(date, offsetMinutes, locale);
    }

    // 使用 Intl.DateTimeFormat 进行时区转换和格式化（适用于 IANA 时区标识符）
    try {
      // 使用 formatToParts 获取各个部分，然后手动拼接，确保格式统一为横杠
      const formatter = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const parts = formatter.formatToParts(date);
      
      // 从 parts 中提取各个部分并手动拼接为斜杠格式
      const year = parts.find(p => p.type === 'year')?.value || '';
      const month = parts.find(p => p.type === 'month')?.value || '';
      const day = parts.find(p => p.type === 'day')?.value || '';
      
      return `${year}/${month}/${day}`;
    } catch (intlError) {
      // 如果 Intl API 不支持该时区，尝试使用偏移量格式
      console.warn('Intl.DateTimeFormat failed, falling back to UTC:', intlError);
      return formatDateWithOffset(date, 0, locale);
    }
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return utcTimeString;
  }
};

